import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, CreditCard, LogOut, Store } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { NAV_ITEMS, PRIMARY_COUNT } from '../app/nav';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { SubscriptionBadge } from '../features/subscription/SubscriptionBadge';

/**
 * Profil — mobil pastki panelning oxirgi tugmasi (ilgari "Yana").
 *
 * Bu yerda: kim kirgani va qaysi do'kon, Obuna bo'limi, pastki panelga
 * sig'magan bo'limlar va Chiqish. Keng ekranda bo'limlar sidebar'da turadi,
 * shuning uchun ro'yxat faqat telefonda ko'rinadi.
 */
export const Profile: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user, seller, rbac, permissions, logout } = useAuthStore();

  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || '';
  const initials = (name || '?').trim().charAt(0).toUpperCase();
  const subscription = seller.subscription;
  const blocked = subscription ? !subscription.active : false;
  const overflowItems = NAV_ITEMS.filter((item) => item.need in rbac).slice(PRIMARY_COUNT);

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Chiqish',
      description: 'Siz rostdan ham chiqishni hohlaysizmi?',
      confirmLabel: 'Ha, chiqaman',
      cancelLabel: "Yo'q",
      danger: true,
    });
    if (!ok) return;
    logout();
    navigate('/login');
  };

  return (
    <div className="space-y-4 max-w-xl">
      <h1 className="page-bar text-xl sm:text-2xl font-bold text-gray-900">Profil</h1>

      <div className="card p-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-800 grid place-items-center text-xl font-bold shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{name}</div>
          {seller.shop && (
            <div className="flex items-center gap-1.5 text-sm text-gray-500 truncate">
              <Store size={14} className="shrink-0" />
              {seller.shop.name}
            </div>
          )}
          {permissions.role_name && <div className="text-xs text-gray-400 mt-0.5">{permissions.role_name}</div>}
        </div>
      </div>

      {subscription && (
        <Link
          to="/profile/obuna"
          className={`card p-4 flex items-center gap-3 active:bg-gray-50 ${blocked ? 'border-danger-100' : ''}`}
        >
          <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
            blocked ? 'bg-danger-50 text-danger-600' : 'bg-primary-50 text-primary-800'
          }`}>
            <CreditCard size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900">Obuna</div>
            <SubscriptionBadge subscription={subscription} />
          </div>
          <ChevronRight size={20} className="text-gray-300" />
        </Link>
      )}

      {overflowItems.length > 0 && !blocked && (
        <div className="card p-1 md:hidden">
          {overflowItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.path} to={item.path}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 active:bg-gray-50">
                <Icon size={20} className="text-gray-400" />
                <span className="flex-1 font-medium text-sm">{item.label}</span>
                <ChevronRight size={18} className="text-gray-300" />
              </Link>
            );
          })}
        </div>
      )}

      <button
        onClick={handleLogout}
        className="card p-4 w-full flex items-center gap-3 text-danger-600 active:bg-danger-50"
      >
        <LogOut size={20} />
        <span className="font-medium text-sm">Chiqish{user?.username ? ` — ${user.username}` : ''}</span>
      </button>
    </div>
  );
};
